import {
	Box,
	Button,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	ResponsiveValue,
	Tooltip
} from "@chakra-ui/react";
import React, { ReactNode } from "react";

interface CustomModalProps {
	isOpen: boolean;
	isCentered?: boolean;
	closeOnOverlayClick?: boolean;
	contentBorder?: boolean;
	contentPadding?: boolean;
	size?: ResponsiveValue<"sm" | "md" | "lg" | "xl" | "2xl" | "xs" | "3xl" | "4xl" | "5xl" | "6xl" | "full"> | undefined;
	title?: string;
	message?: string;
	content?: ReactNode;
	footerContent?: ReactNode;
	cancelText?: string | null;
	continueText?: string;
	continueTooltip?: string;
	continueDisabled?: boolean;
	continueLoading?: boolean;
	continueColorScheme?: string;
	onContinue: () => void;
	onCancel?: () => void;
}

/**
 * A reuseable confirmation modal that can display different messages and handle continue or cancel actions.
 *
 * @param isOpen a boolean of if the modal is open or not.
 * @param isCentered a boolean of if the modal should be vertically centered or not. Defaults to true.
 * @param closeOnOverlayClick a boolean of if the modal should close when the overlay background is clicked. Defaults to true.
 * @param contentBorder a boolean of if the content should have a border around it. Defaults to false.
 * @param contentPadding a boolean of if the content should have padding around it. Defaults to true.
 * @param size the width size of the modal. Defaults to "sm".
 * @param title an optional title to display as the modal header.
 * @param message an optional string of text to display as the prompt.
 * @param content an optional react node to display as the prompt.
 * @param footerContent an optional react node to display additional footer content.
 * @param cancelText the text to display on the cancel button. If null or an empty string, the button is hidden. Defaults to "Cancel".
 * @param continueText the text to display on the continue button. Defaults to "Yes".
 * @param continueTooltip the tooltip to display when hovering over the continue button.
 * @param continueDisabled whether or not the continue button is disabled. Defaults to false.
 * @param continueLoading whether or not the continue button is loading. Defaults to false.
 * @param continueColorScheme the color scheme of the continue button. Defaults to "orange".
 * @param onContinue a required callback function when the continue button is clicked.
 * @param onCancel an optional callback function when the close button is clicked.
 */
export default function CustomModal({
	isOpen,
	isCentered = true,
	closeOnOverlayClick = true,
	contentBorder = false,
	contentPadding = true,
	size = "sm",
	title,
	message,
	content,
	footerContent,
	cancelText = "Cancel",
	continueText = "Yes",
	continueTooltip,
	continueDisabled = false,
	continueLoading = false,
	continueColorScheme = "orange",
	onContinue,
	onCancel
}: CustomModalProps) {
	const onClose = () => {
		if (typeof onCancel == "function") {
			onCancel();
		}
	};

	return isOpen ? (
		<Modal
			isOpen={isOpen}
			isCentered={isCentered}
			onClose={onClose}
			size={size}
			closeOnOverlayClick={closeOnOverlayClick}>
			<ModalOverlay />
			<ModalContent fontWeight={"normal"}>
				<ModalHeader paddingY="2">{title}</ModalHeader>
				{typeof onCancel == "function" && <ModalCloseButton />}
				<ModalBody
					maxHeight="60vh"
					overflow="auto"
					borderTop={title ? "2px solid" : "none"}
					borderTopColor={title ? (contentBorder ? "gray.300" : "white") : "transparent"}
					borderBottom="2px solid"
					borderBottomColor={contentBorder ? "gray.300" : "white"}
					py="0px"
					px={contentPadding ? "6" : "0"}>
					{message && (
						<Box justifyContent="space-between" display="flex" pt={3}>
							{message}
						</Box>
					)}
					{!!content && content}
				</ModalBody>
				<ModalFooter>
					{typeof onCancel == "function" && !!cancelText && cancelText !== "" && (
						<Button variant="ghost" mr="5" onClick={onClose}>
							{cancelText}
						</Button>
					)}
					{!!footerContent && footerContent}
					<Tooltip label={continueTooltip ?? ""}>
						<Button
							colorScheme={continueColorScheme}
							type="submit"
							isDisabled={continueDisabled || continueLoading}
							isLoading={continueLoading}
							autoFocus={true}
							onClick={() => onContinue()}>
							{continueText}
						</Button>
					</Tooltip>
				</ModalFooter>
			</ModalContent>
		</Modal>
	) : (
		<></>
	);
}
